// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomErrorClass, ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { Rectangle } from '@shared/commons/Geometry';
import { LinkedSystem } from '../misc/LinkedSystem';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import AdvancedOrderEntryTemplate from '@front/templates/AdvancedOrderEntryTemplate.html';
import OrderEditControlsTemplate from '@front/templates/OrderEditControlsTemplate.html';
import { DockSystemInstance } from '../DockSystem';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { OrderEditViewBase } from './OrderEditViewBase';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { Account } from '@shared/commons/cache/Account';
import { InstrumentSpecificType } from '@shared/utils/Instruments/InstrumentSpecificType';
import { DynProperty } from '@shared/commons/DynProperty';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { Instrument } from '@shared/commons/cache/Instrument';
import { IsAllowed, IsAllowedResponce } from '@shared/commons/IsAllowed';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { OrderEditUpdateData } from '@shared/utils/Trading/OrderEditUpdateData';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { TradingNumericErrorChecker } from '@shared/commons/Trading/TradingNumericErrorChecker';
import { OrderExecutorUtils } from '@shared/commons/Trading/OrderExecutorUtils';
import { MathUtils } from '@shared/utils/MathUtils';
import { TIF } from '@shared/utils/Trading/OrderTif';
import { WDSettings } from '../settings/WDGeneralSettings';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { HotkeysManager } from '@shared/commons/cache/Hotkeys/HotkeysManager';
import { OrderEntryHotkeysEnum } from '@shared/commons/cache/Hotkeys/NamesEventHotkey ';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
import { TextColor } from '@components/webdesktop/elements/Label/TextColor';
import { OrderTif } from '@shared/utils/Trading/OrderTifEnum';
// TODO. Optimize. DynProperty + js properties interface for orderEdit parameters.
// Something like NotificationObserver in MVVM .NET.
// OrderEdit parameter <-> SINGLE reusable DynProperty per Parameter <-> Ractive Control.
export class AdvancedOrderEntry extends OrderEditViewBase {
    constructor() {
        super();
        this.headerLocaleKey = 'panel.orderEntry';
        this.NewParametrsHandler = null;
        this.closeTimeout = null;
        this.AutoHideHeaderUpdateHandler = null;
        this.IsResized = false;
        this.width = 400;
        this.widthMarginOE = 800;
        this.onSideChanged = (side) => {
            void this.set('side', side);
        };
    }
    getType() {
        return PanelNames.AdvancedOrderEntry;
    }
    oninit() {
        super.oninit();
        this.observe('marginOEVisible', this.marginOEVisibleChanged, { init: false });
        this.observe('instrument', this.onInstrumentChanged);
        this.observe('account', this.onAccountChanged);
        void this.set('accountVisible', DataCache.getNumberOfAccounts() > 1);
        this.observe('orderType', this.onOrderTypeChanged);
        this.observe('instrument account quantity estQtyValue cash isCashQtyMode side tif productType disclosedQuantity positionSizingChecked leverageValue userComment isAllowOTHTrading', this.onTradingDataChanged);
        this.observe('positionSizingChecked', this.updatePositionSizingControls);
        this.observe('instrument account isCashQtyMode', this.turnOffPositionSizing);
        this.observe('isCashQtyMode', this.needUpdateAllowedOrderTypes);
        this.on('placeOrderClick', this.onPlaceOrderClick);
        this.on('btnCancelClick', () => {
            this.close();
        });
        if (!Resources.isHidden('OrderEntry.InfoBlock.Visibility')) {
            void this.set('width', this.width);
            void this.set('marginOEAllowed', true);
            this.on('onMarginOEVisibleStateChanged', this.onMarginOEVisibleStateChanged);
        }
        void this.set('positionSizingVisible', DataCache.isAllowedForMyUser(RulesSet.FUNCTION_POSITION_SIZING));
        this.observe('delayedVisible snapshotVisible dataSourceVisible', this.deferLayout);
        this.observe('dataSourceOpen', this.onDataSourceOpen);
        DataCache.OnUpdateAccount.Subscribe(this.onUpdateAccount, this);
        DataCache.OnUpdateInstrument.Subscribe(this.onUpdateInstrument, this);
    }
    oncomplete() {
        super.oncomplete();
        this.deserializeDelayedData();
        void this.set({
            isAllowAttachDetachPanel: DockSystemInstance.isAutoHideAvaliable(),
            reverseOrderButtons: WDSettings.reverseButtonsOrder
        });
        DockSystemInstance.RegisterPanel(this);
        this.OnResize.Subscribe(this.resizingProcess, this);
        KeyEventProcessor.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
        if (this.get('isCreateOrderParmeters')) {
            void this.set({
                isAllowAttachDetachPanel: false,
                isAccountLinkShow: false,
                isSymbolLinkShow: false,
                canLinkByAccount: false
            });
        }
        this.setAttached(this.get('attached'));
        this.marginOEVisibleChanged(this.get('marginOEVisible'));
        this.marginOEResizeObserver = new ResizeObserver(this.layoutMarginOE.bind(this));
        this.marginOEResizeObserver.observe(this.find('.advanced-order-entry'));
        this.SetFirstElement();
        this.center();
        this.addScrollToContainer('.advanced-order-entry-list-wrapper');
    }
    updatePanelHeader() {
        const ins = this.get('instrument');
        const header = Resources.getResource(this.headerLocaleKey) + (!isNullOrUndefined(ins) ? ' ' + ins.DisplayName() : '');
        void this.set({ header });
        if (!isNullOrUndefined(this.AutoHideHeaderUpdateHandler)) {
            this.AutoHideHeaderUpdateHandler(header);
        }
    }
    center() {
        if (this.get('left') === 0 && this.get('top') === 0) {
            super.center();
        }
    }
    onInstrumentChanged(instrument, lastInstrument) {
        if (isNullOrUndefined(instrument) || instrument === lastInstrument) {
            return;
        }
        this.subscribeRiskSettingsUpdate(instrument, lastInstrument);
        this.instrumentSettingsUpdate();
        this.isShowCommentSelector();
        this.updateIsVisibleOTHTrading();
        this.symbolLink_Out(false, instrument);
        this.updatePanelHeader();
        this.deferLayout();
    }
    onAccountChanged(account, lastAccount) {
        if (isNullOrUndefined(account)) {
            return;
        }
        this.instrumentSettingsUpdate(); // for productTypeShow and others update
        this.updateIsVisibleOTHTrading();
        this.accountLink_Out(false, account);
    }
    resizingProcess() {
        this.IsResized = true;
        this.layoutMarginOE();
    }
    UpdateAutoHideSate(state) {
        if (this.get('attached') || this.get('isCreateOrderParmeters')) {
            return;
        }
        void this.set('isAllowAttachDetachPanel', state);
    }
    ForcedAutoHide() {
        if (!this.get('attached')) {
            return;
        }
        clearTimeout(this.closeTimeout);
        void this.set({ visible: false });
        MainWindowManager.MainWindow.resetFocus();
    }
    AttachClick(_event) {
        const attached = this.get('attached');
        if (attached) {
            clearTimeout(this.closeTimeout);
        }
        this.setAttached(!attached);
    }
    setAttached(attached) {
        if (attached) {
            this.IsResized = false;
            void this.set({ visible: false, movable: false, attached: true, resizable: false });
            DockSystemInstance.addToAutoHide(this);
        }
        else {
            void this.set({ visible: true, movable: true, attached: false, resizable: false });
            if (this.get('marginOEVisible') && !isNullOrUndefined(this.Controls.marginOEControl)) {
                void this.set('resizable', true);
            }
            DockSystemInstance.removeFromAutoHide(this);
        }
        this.layout();
        if (!attached) {
            this.center();
        }
        MainWindowManager.MainWindow.resetFocus();
        this.layoutMarginOE();
        const btnattachText = attached
            ? Resources.getResource('panel.external.Dettach')
            : Resources.getResource('panel.external.Attach');
        void this.set('attachDetachPanelText', btnattachText);
    }
    AutoHideHover() {
        clearTimeout(this.closeTimeout);
        const visible = this.get('visible');
        if (visible) {
            return;
        }
        void this.set({ visible: true });
        this.ChangeAutohideLocation();
    }
    onGlobalKeyDown(key) {
        if (this.get('focused')) {
            if (key === KeyCode.ESC) {
                this.close();
            }
        }
    }
    AutoHideLeave() {
        if (this.get('attached')) {
            this.MakeMeHidden();
        }
    }
    MakeMeHidden() {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = setTimeout(() => {
            if (!this.get('focused')) {
                void this.set({ visible: false });
            }
            clearTimeout(this.closeTimeout);
        }, AdvancedOrderEntry.TIME_TO_HIDE);
    }
    ChangeAutohideLocation() {
        const newCoord = ControlsUtils.CorrectPopupLocation(new Rectangle(2000, 52, this.get('width'), this.get('height')));
        this.setLocation(newCoord.newX - 27, newCoord.newY);
    }
    lostFocus() {
        var _a, _b;
        super.lostFocus();
        if (!this.get('attached')) {
            return;
        }
        if ((_a = MainWindowManager.TerceraInstrumentLookupDropDownForm) === null || _a === void 0 ? void 0 : _a.get('visible')) {
            MainWindowManager.TerceraInstrumentLookupDropDownForm.AfterLostFocusWaiting = this;
            return;
        }
        if ((_b = MainWindowManager.TerceraAccountLookupDropDownForm) === null || _b === void 0 ? void 0 : _b.get('visible')) {
            MainWindowManager.TerceraAccountLookupDropDownForm.AfterLostFocusWaiting = this;
            return;
        }
        this.MakeMeHidden();
    }
    onMouseEnter(event) {
        this.AutoHideHover();
    }
    onMouseLeave(event) {
        this.AutoHideLeave();
    }
    gotFocus() {
        super.gotFocus();
        clearTimeout(this.closeTimeout);
    }
    dispose() {
        if (this.get('attached')) {
            DockSystemInstance.removeFromAutoHide(this);
        }
        DockSystemInstance.UnRegisterPanel(this);
        KeyEventProcessor.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
        DataCache.OnUpdateAccount.UnSubscribe(this.onUpdateAccount, this);
        DataCache.OnUpdateInstrument.UnSubscribe(this.onUpdateInstrument, this);
        const instrument = this.get('instrument');
        if (!isNullOrUndefined(instrument)) {
            // TODO! в AdvancedOrderEntry нет метода updateTradingAllowedStuff, заменим на this.instrumentSettingsUpdate ?
            instrument.RiskSettingsUpdated.UnSubscribe(this.updateTradingAllowedStuff, this);
        }
        this.OnResize.UnSubscribe(this.resizingProcess, this);
        this.marginOEResizeObserver.disconnect();
        OrderEditViewBase.prototype.dispose.call(this);
    }
    SetFirstElement() {
        if (this.get('accountVisible')) {
            this.Controls.OEAccountLookup.setFocus();
        }
        else {
            this.Controls.OEInstrumentLookup.setFocus();
        }
    }
    onPlaceOrderClick() {
        if (this.get('isCreateOrderParmeters')) {
            this.saveOrderParam();
        }
        else {
            if (TradingNumericErrorChecker.HasErrors(this)) {
                return;
            }
            this.placeOrder(this.orderEdit);
        }
    }
    selectOrderType(newOrderType) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const forbiddenOrderTypes = this.get('forbiddenOrderTypes');
            const orderTypeDict = OrderExecutorUtils.getAllowedOrderTypeDict(this.get('account'), this.get('instrument'), forbiddenOrderTypes, WDSettings.orderType, false);
            const orderType = (_a = orderTypeDict[newOrderType]) !== null && _a !== void 0 ? _a : orderTypeDict[OrderType.Market];
            const selectedItem = { value: orderType, text: Resources.getResource(orderType.localizationKey()) };
            yield this.set('selOrderTypeCBItem', selectedItem);
        });
    }
    onOrderTypeChanged(newOrderType, oldOrderType) {
        if (isNullOrUndefined(newOrderType) ||
            newOrderType === oldOrderType ||
            (newOrderType === null || newOrderType === void 0 ? void 0 : newOrderType.id()) === (oldOrderType === null || oldOrderType === void 0 ? void 0 : oldOrderType.id())) {
            return;
        }
        this.isShowCommentSelector();
        const orderEdit = newOrderType.createOrderEditObject({
            dataCache: DataCache
        });
        this.setOrderEdit(orderEdit);
        this.addDisclosedQuantityIfNeed();
        orderEdit.updateParameters(new OrderEditUpdateData(null, this.getAllTradingDataDict()));
        this.updatePositionSizingControls();
    }
    // TODO. Rename; Parameters' constants.
    onTradingDataChanged(newVal, oldVal, key) {
        if (newVal === oldVal) {
            return;
        }
        if (isNullOrUndefined(newVal) && isNullOrUndefined(oldVal)) {
            return;
        }
        if (typeof newVal === 'number' && typeof oldVal === 'number' && isNaN(newVal) && isNaN(oldVal)) {
            return;
        }
        const orderEdit = this.orderEdit;
        if (isNullOrUndefined(orderEdit))
            return;
        const tradingDataDict = {};
        tradingDataDict[key] = newVal;
        this.addDisclosedQuantityIfNeed(key);
        orderEdit.updateParameters(new OrderEditUpdateData(null, tradingDataDict));
        this.deferLayout();
    }
    // TODO. Rename; Parameters' constants.
    getAllTradingDataDict() {
        const tradingDataDict = {};
        const instrument = this.get('instrument');
        if (!isNullOrUndefined(instrument))
            tradingDataDict.instrument = instrument;
        const account = this.get('account');
        if (!isNullOrUndefined(account))
            tradingDataDict.account = account;
        const quantity = this.get('quantity');
        if (!isNullOrUndefined(quantity))
            tradingDataDict.quantity = quantity;
        const estQtyValue = this.get('estQtyValue');
        if (!isNullOrUndefined(estQtyValue))
            tradingDataDict.estQtyValue = estQtyValue;
        const cash = this.get('cash');
        if (!isNullOrUndefined(cash))
            tradingDataDict.cash = cash;
        const isCashQtyMode = this.get('isCashQtyMode');
        if (!isNullOrUndefined(isCashQtyMode))
            tradingDataDict.isCashQtyMode = isCashQtyMode;
        const tif = this.get('tif');
        if (!isNullOrUndefined(tif))
            tradingDataDict.tif = tif;
        const side = this.get('side');
        if (side !== null)
            tradingDataDict.side = side;
        const productType = this.get('productType');
        if (productType !== null)
            tradingDataDict.productType = productType;
        const leverageValue = this.get('leverageValue');
        if (isValidNumber(leverageValue) && leverageValue !== 0)
            tradingDataDict.leverageValue = leverageValue;
        const disclosedQuantity = this.get('disclosedQuantity');
        if (disclosedQuantity !== null)
            tradingDataDict.disclosedQuantity = disclosedQuantity;
        const positionSizingChecked = this.get('positionSizingChecked');
        if (!isNullOrUndefined(positionSizingChecked))
            tradingDataDict.positionSizingChecked = positionSizingChecked;
        const userComment = this.get('userComment');
        if (isValidString(userComment))
            tradingDataDict.userComment = userComment;
        const isAllowOTHTrading = this.get('isAllowOTHTrading');
        if (!isNullOrUndefined(isAllowOTHTrading)) {
            tradingDataDict.isAllowOTHTrading = isAllowOTHTrading;
        }
        return tradingDataDict;
    }
    layout() {
        this.updateOrderEditParametersVisibility();
        if (this.get('attached')) {
            void this.set('height', MainWindowManager.MainWindow.GetAvaliableHeight());
        }
        else {
            void this.set('height', null);
        }
        // Adjust the panel if it overflows the screen #124527
        this.resizePanelToFitScreen();
    }
    saveOrderParam() {
        if (!isNullOrUndefined(this.NewParametrsHandler)) {
            this.NewParametrsHandler(this.orderEdit);
        }
        this.close();
    }
    placeOrder(orderEdit) {
        orderEdit.placedFrom = PlacedFrom.WEB_OE;
        DataCache.FOrderExecutor.placeOrderPromise(orderEdit, null, null, this.focusWarningNumeric.bind(this)).catch(function () {
            const ex = new CustomErrorClass('AdvancedOrderEntry error', 'AdvancedOrderEntry.placeOrder', 'placeOrder -> placeOrderPromise');
            ErrorInformationStorage.GetException(ex);
        });
    }
    updateSettings() {
        const ins = this.get('instrument');
        const acc = this.get('account');
        if (isNullOrUndefined(ins) || isNullOrUndefined(acc)) {
            return;
        }
        void this.set('reverseOrderButtons', WDSettings.reverseButtonsOrder);
        this.updateTradingAllowedAndReasonTooltip();
    }
    // #region ICaller
    callBack(properties) {
        super.callBack(properties);
        if (!this.completed) {
            return;
        }
        void this.set('instrument', this.getCallBackInstrument(properties, 'symbol'));
        let dp = DynProperty.getPropertyByName(properties, 'account');
        if ((dp === null || dp === void 0 ? void 0 : dp.value) != null) {
            void this.set('account', SessionSettings.getDefValueFromObj(DataCache.Accounts[dp.value], DataCache.Accounts));
        }
        dp = DynProperty.getPropertyByName(properties, 'marginOEVisible');
        if (!isNullOrUndefined(dp)) {
            void this.set('marginOEVisible', dp.value);
        }
        dp = DynProperty.getPropertyByName(properties, 'dataSourceOpen');
        if (!isNullOrUndefined(dp)) {
            void this.set('dataSourceOpen', dp.value);
        }
        dp = DynProperty.getPropertyByName(properties, 'attached');
        if (!isNullOrUndefined(dp)) {
            void this.set('attached', dp.value);
        }
    }
    Properties() {
        const properties = super.Properties();
        const ins = this.get('instrument');
        if (!isNullOrUndefined(ins)) {
            properties.push(new DynProperty('symbol', ins.GetInteriorID(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        const acc = this.get('account');
        if (!isNullOrUndefined(acc)) {
            properties.push(new DynProperty('account', acc.AcctNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        const marginOEVisible = this.get('marginOEVisible');
        properties.push(new DynProperty('marginOEVisible', marginOEVisible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        const dataSourceOpen = this.get('dataSourceOpen');
        properties.push(new DynProperty('dataSourceOpen', dataSourceOpen, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('attached', this.get('attached'), DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        return properties;
    }
    // #endregion
    // #region Link
    symbolLink_Out(newSubscriber, instrument) {
        if (isNullOrUndefined(instrument)) {
            const ins = this.get('instrument');
            if (isNullOrUndefined(ins))
                return;
            instrument = ins;
        }
        const color = this.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrument.GetInteriorID(), newSubscriber);
        }
    }
    symbolLink_In(symbolName) {
        const newInstr = DataCache.getInstrumentByName(symbolName);
        if (!isNullOrUndefined(newInstr)) {
            void this.set('instrument', newInstr);
        }
    }
    // #endregion Link
    localize() {
        super.localize();
        void this.set('disclosedLabel', Resources.getResource('panel.newOrderEntry.disclosedLabel'));
        void this.set('buttonCancelText', Resources.getResource('general.messageBox.cancel'));
        void this.set('delayedText', Resources.getResource('panel.newOrderEntry.delayedText'));
        void this.set('snapshotText', Resources.getResource('panel.newOrderEntry.snapshotText'));
        void this.set('sideLabel', Resources.getResource('panel.newOrderEntry.Side'));
    }
    onUpdateAccount(account) {
        const orderEdit = this.orderEdit;
        if (isNullOrUndefined(orderEdit))
            return;
        orderEdit.onUpdateAccount(account);
    }
    onUpdateInstrument(instrument) {
        const myInstrument = this.get('instrument');
        if (!isNullOrUndefined(myInstrument) && myInstrument === instrument) {
            this.updateTradingAllowedAndReasonTooltip();
        }
    }
    instrumentSettingsUpdate() {
        const instrument = this.get('instrument');
        const productType = this.get('productType');
        const account = this.get('account');
        if (!Instrument.IsWorkingInstrument(instrument)) {
            return;
        }
        if (!isNullOrUndefined(account)) {
            this.updateTradingAllowedAndReasonTooltip();
        }
        void this.set('productTypeShow', instrument.isProductTypeVisible(account));
        void this.set('leverageShow', instrument.isLeverageVisible(account, productType));
        this.updateDelayedAndSnapshotVisibility();
    }
    updateTradingAllowedAndReasonTooltip() {
        const instrument = this.get('instrument');
        const account = this.get('account');
        const ordertype = this.get('orderType');
        let ordT = -1;
        if (!isNullOrUndefined(ordertype)) {
            ordT = ordertype.id();
        }
        let tradingAllowed = IsAllowed.IsTradingAllowed(account, instrument, ordT);
        if (this.get('isCreateOrderParmeters')) {
            tradingAllowed = IsAllowedResponce.AllowedResponce();
        }
        if (tradingAllowed.Allowed && ordT === -1) {
            tradingAllowed.ReasonText = Resources.getResource('Not selected order type');
            tradingAllowed.Allowed = false;
        }
        void this.set({
            isAllowedResponce: tradingAllowed,
            tradingAllowed: tradingAllowed.Allowed,
            tradingForbiddenReason: tradingAllowed.ReasonText
        });
    }
    subscribeRiskSettingsUpdate(instrument, lastInstrument) {
        if (!isNullOrUndefined(lastInstrument)) {
            lastInstrument.RiskSettingsUpdated.UnSubscribe(this.instrumentSettingsUpdate, this);
        }
        if (!isNullOrUndefined(instrument)) {
            instrument.RiskSettingsUpdated.Subscribe(this.instrumentSettingsUpdate, this);
        }
    }
    onMarginOEVisibleStateChanged() {
        this.set('marginOEVisible', !this.get('marginOEVisible'));
    }
    marginOEVisibleChanged(isVisible) {
        void this.set('width', isVisible ? this.widthMarginOE : this.width);
        if (!this.get('attached')) {
            void this.set('resizable', isVisible);
        }
        if (!isVisible) {
            this.IsResized = false;
            this.layout();
        }
        if (this.get('attached')) {
            this.ChangeAutohideLocation();
        }
        this.layoutMarginOE();
    }
    onQuantityChanged(qtyValue) {
        const orderEdit = this.orderEdit;
        if (isNullOrUndefined(orderEdit))
            return;
        const ins = orderEdit.instrument;
        if (isNullOrUndefined(ins))
            return;
        const OEQty = !isNullOrUndefined(this.Controls) ? this.Controls.OEQuantity : null;
        const acc = orderEdit.account;
        const pt = orderEdit.productType;
        const isAllowedResponce = this.get('isAllowedResponce');
        if (isAllowedResponce === null || isAllowedResponce === void 0 ? void 0 : isAllowedResponce.Allowed) {
            // доп валидацию по qty посчитанную position sizing-ом можно применить если торговля разрешена в IsAllowed.IsTradingAllowed
            const inLots = !isNullOrUndefined(orderEdit.sessionSettings) ? WDSettingsUtils.displayAmountInLots() : null;
            const minLot = Quantity.convertQuantityValue(new Quantity(ins.getMinLot(pt, acc), true), ins, inLots, acc, pt);
            const maxLot = Quantity.convertQuantityValue(new Quantity(ins.getMaxLot(pt, acc), true), ins, inLots, acc, pt);
            const tradingAllowed = qtyValue >= minLot && qtyValue <= maxLot;
            const reasonLocKey = tradingAllowed
                ? ''
                : 'positionSizeCalculator.OEButtonDisabled.' + (qtyValue < minLot ? 'less' : 'more');
            let reasonText = Resources.getResource(reasonLocKey);
            if (!tradingAllowed) {
                reasonText = reasonText.replace('{0}', (qtyValue < minLot ? minLot : maxLot).toString());
            }
            void this.set({
                tradingAllowed,
                tradingForbiddenReason: reasonText
            });
        }
        if (!isNullOrUndefined(OEQty)) {
            void OEQty.set('value', qtyValue);
            OEQty.updateQuantity(); /// // TODO !!!!! TODO !!!!!!!!!!!!!!!!!!!!!!!!!
        }
        if (this.get('positionSizingChecked')) {
            this.flushQuantity();
        }
    }
    flushQuantity() {
        void this.set('qtyCustomCSSClass', 'js-flushed-qty-style');
        if (!isNullOrUndefined(this.flushQtyTimer)) {
            clearTimeout(this.flushQtyTimer);
        }
        this.flushQtyTimer = setTimeout(() => {
            void this.set('qtyCustomCSSClass', '');
        }, 1000);
    }
    updatePositionSizingControls() {
        // обновить подсветку лейблов, участвующих в positionSizing-e, включить SL
        if (isNullOrUndefined(this.orderEdit))
            return;
        const checked = this.get('positionSizingChecked');
        setTimeout(() => {
            if (isNullOrUndefined(this.orderEdit))
                return;
            this.enableSL(checked);
            this.orderEdit.customCSS = checked ? 'js-flushed-labels-for-position-sizing' : '';
            const paramArray = this.get('orderEditParameterArray');
            for (let i = 0; i < paramArray.length; i++) {
                if (!this.orderEdit.skipParamForFlashing(paramArray[i].name)) {
                    paramArray[i].customCSS = checked ? 'js-flushed-labels-for-position-sizing' : '';
                }
            }
            void this.set('orderEditParameterArray', paramArray);
        }, 1);
    }
    turnOffPositionSizing(newData, oldData) {
        // #100580
        if (isNullOrUndefined(newData) || isNullOrUndefined(oldData)) {
            return;
        }
        if (newData instanceof Instrument && newData.GetInteriorID() === oldData.GetInteriorID()) {
            // инструменты
            return;
        } // #100615 коммент
        if (newData instanceof Account && newData.AcctNumber === oldData.AcctNumber) {
            // аккаунты
            return;
        }
        void this.set('positionSizingChecked', false);
    }
    enableSL(checked) {
        const orderEdit = this.orderEdit;
        if (!orderEdit) {
            return;
        }
        orderEdit.setSLForcedState(checked);
    }
    updateDelayedAndSnapshotVisibility() {
        let instrument = this.get('instrument');
        if (isNullOrUndefined(instrument)) {
            return;
        }
        if (instrument.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract) {
            instrument = DataCache.getInstrument(instrument.InstrumentTradableID.toString(), instrument.Route);
        } // #100762
        const canGetSnapshot = instrument.CanGetSnapshot();
        const delayMoreThanZero = instrument.HasDelay();
        const subsribeErrorCode = instrument.HasLvl1SubscriptionErrorCode;
        let delayedVisible = delayMoreThanZero; // 5), 6), 7), 8)
        let snapshotVisible = canGetSnapshot; // 1), 2), 3), 4), 6), 7), 8)
        // 1) delay > 0 && snapshot == TRUE && error code = true - ПОКАЗЫВАТЬ КНОПКУ, НЕ ПОКАЗЫВАТЬ DELAYED DATA - не реальный кейс
        // 2) delay > 0 && snapshot == TRUE &&  error code = false - ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA
        // 3) delay > 0 && snapshot == FALSE && error code = false - НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA
        // 4) delay > 0 && snapshot == FALSE &&  error code = true - НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA
        // 5) delay = 0 && snapshot == TRUE && error code = true - ПОКАЗЫВАТЬ КНОПКУ, НЕ ПОКАЗЫВАТЬ DELAYED DATA
        // 6) delay = 0 && snapshot == TRUE && error code = false - НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA
        // 7) delay = 0 && snapshot == FALSE && error code = false - НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA
        // 8) delay = 0 && snapshot == FALSE && error code = true - НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA
        if (delayMoreThanZero) {
            if (canGetSnapshot) {
                // 1), 2)
                delayedVisible = !subsribeErrorCode;
            }
            else {
                // 3), 4)
                delayedVisible = false;
            } // 5)
        }
        else if (canGetSnapshot) {
            snapshotVisible = subsribeErrorCode;
        }
        void this.set({
            snapshotVisible,
            delayedVisible
        });
    }
    onDataSourceOpen() {
        this.deferLayout();
        const marginOE = this.Controls.marginOEControl;
        if (!isNullOrUndefined(marginOE)) {
            setTimeout(() => {
                marginOE.UpdateTable();
            }, 100);
        }
    }
    fillByOrderEdit(orderEdit) {
        return __awaiter(this, void 0, void 0, function* () {
            if (MathUtils.IsNullOrUndefined(orderEdit)) {
                return;
            }
            yield this.set({ instrument: orderEdit.instrument, account: orderEdit.account });
            yield this.selectOrderType(orderEdit.getOrderTypeId());
            yield this.set({
                side: orderEdit.side,
                defaultTif: orderEdit.tif,
                productType: orderEdit.productType,
                defaultQuantity: orderEdit.quantity,
                disclosedQuantity: orderEdit.disclosedQuantity,
                leverageValue: orderEdit.leverageValue,
                isAllowOTHTrading: orderEdit.isAllowOTHTrading,
                cash: orderEdit.cash,
                isCashQtyMode: orderEdit.cash > 0
            });
            this.setOrderEdit(orderEdit);
        });
    }
    applyParameters(orderEntryParameters) {
        return __awaiter(this, void 0, void 0, function* () {
            const account = !isNullOrUndefined(orderEntryParameters.account)
                ? orderEntryParameters.account
                : this.get('account');
            const instrument = !isNullOrUndefined(orderEntryParameters.instrument)
                ? orderEntryParameters.instrument
                : this.get('instrument');
            yield this.set({ account, instrument });
            yield this.set({
                side: orderEntryParameters.isLong ? OperationType.Buy : OperationType.Sell,
                defaultQuantity: new Quantity(orderEntryParameters.quantity, true)
            });
        });
    }
    applyClosePositionViaOEParameters(orderEntryParameters) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.applyParameters(orderEntryParameters);
            const limitOrderType = OrderExecutorUtils.getAllowedOrderTypeDict(this.get('account'), this.get('instrument'), this.get('forbiddenOrderTypes'), WDSettings.orderType, false)[OrderType.Limit];
            if (!isNullOrUndefined(limitOrderType)) {
                yield this.selectOrderType(OrderType.Limit);
            }
            yield this.set('defaultTif', new TIF(OrderTif.Day));
            const orderEdit = this.orderEdit;
            if (isValidNumber(orderEntryParameters.limitPrice) && (orderEdit === null || orderEdit === void 0 ? void 0 : orderEdit.getOrderTypeId()) === OrderType.Limit) {
                orderEdit.setBasePrice(orderEntryParameters.limitPrice);
            }
        });
    }
    HotkeyPressed(hotkey) {
        const isPressed = super.HotkeyPressed(hotkey);
        if (isPressed) {
            return true;
        }
        const hotkeyAction = HotkeysManager.GetActionByHotkeyForPanel(hotkey, this.getType());
        switch (hotkeyAction) {
            case OrderEntryHotkeysEnum.PlaceOrder:
                this.placeOrder(this.orderEdit);
                return true;
        }
        return false;
    }
    themeChange() {
        const marginOE = this.findComponent('marginOEControl');
        marginOE === null || marginOE === void 0 ? void 0 : marginOE.callThemeChange();
    }
    needUpdateAllowedOrderTypes(isCashQtyMode) {
        if (this.orderEdit != null) {
            this.orderEdit.skipSessionSettingsChanged = true;
        }
        const forbiddenOrderTypes = isCashQtyMode ? [OrderType.OCO] : [];
        void this.set({ forbiddenOrderTypes });
    }
    layoutMarginOE() {
        if (!this.get('marginOEVisible') || isNullOrUndefined(this.Controls.marginOEControl)) {
            return;
        }
        this.Controls.marginOEControl.layoutTable();
    }
}
AdvancedOrderEntry.TIME_TO_HIDE = 1000;
OrderEditViewBase.extendWith(AdvancedOrderEntry, {
    partials: {
        bodyPartial: AdvancedOrderEntryTemplate,
        orderEditControls: OrderEditControlsTemplate
    },
    data() {
        return {
            DynProperty,
            TextColor,
            showFooter: false,
            resizable: false,
            minWidth: 570,
            dockablePanel: false,
            showHeader: true,
            isAccountLinkShow: false,
            isSymbolLinkShow: true,
            attached: false,
            style_addition_body: 'advanced-order-entry-additional-body-style',
            instrument: null,
            account: null,
            quantity: null, // QuantityObject
            side: OperationType.Buy,
            tif: null, // TIF object
            orderType: null,
            productType: null,
            productTypeShow: false,
            leverageShow: false,
            enableComment: false,
            userComment: '',
            disclosedQuantityShow: false,
            disclosedQuantity: null,
            dqMinValue: null,
            dqMaxValue: null,
            dqTooltip: 'panel.newOrderEntry.disclosedLabel.tt',
            dqLessMinLocalKey: 'UserControl.TerceraDQNumeric.ValueLessMin',
            dqGreaterMaxLocalKey: 'UserControl.TerceraDQNumeric.ValueGreaterMax',
            disclosedLabel: '',
            reverseOrderButtons: false,
            accountVisible: false, // resulting account lookup visibility
            isEnabledInstrumentLookup: true,
            isEnabledAccountLookup: true,
            canFilterByAccount: false,
            selOrderTypeCBItem: null,
            isCreateOrderParmeters: false,
            placeButtonAdditionalClass: '',
            placeButtonLocalizationKey: '',
            isCashQtyMode: false, // #122525
            estQtyValue: null,
            positionSizingVisible: false,
            positionSizingChecked: false,
            delayedVisible: false,
            snapshotVisible: false,
            delayedText: '',
            snapshotText: '',
            dataSourceVisible: false, // true to enable data source
            dataSourceOpen: false,
            marginOEAllowed: false,
            marginOEVisible: false,
            forbiddenOrderTypes: [],
            style_addition_header: 'attach-detach-panel-header',
            sideLabel: '',
            allowHeightResize: false
        };
    },
    computed: {
        marginOEButtonTooltip() {
            return this.get('marginOEVisible')
                ? Resources.getResource('panel.orderEntry.marginCollapse.tooltip')
                : Resources.getResource('panel.orderEntry.marginExpand.tooltip');
        }
    }
});
